import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { TreeDeciduous, Network, GitBranch, Orbit, Target, LayoutGrid, ChevronDown, Columns2 } from 'lucide-react';
import { colors, text } from '@annote/core/styles';
import { useProfileContext } from 'src/context/ProfileContext';
import { useIconSize } from 'src/context/SettingsContext';
import { NAV_ICON_VISUAL_SIZE } from './PanelToggleButton';

type SurfaceType = 'Tree' | 'Graph' | 'Focus' | 'Flow D3 Force' | 'Flow' | 'Source Grid' | 'ReadWrite';

const allSurfaceTypesMap: Record<SurfaceType, string> = {
  Tree: 'tree',
  Graph: 'graph',
  Focus: 'focus',
  'Flow D3 Force': 'flowd3force',
  Flow: 'flow',
  'Source Grid': 'sourcegrid',
  ReadWrite: 'readwrite',
};

const surfaceIcons: Record<SurfaceType, React.FC<{ size?: number }>> = {
  Tree: TreeDeciduous,
  Graph: Network,
  Focus: Target,
  'Flow D3 Force': Orbit,
  Flow: GitBranch,
  'Source Grid': LayoutGrid,
  ReadWrite: Columns2,
};

const slug2SurfaceType = (slug: string): SurfaceType | undefined => {
  const entry = Object.entries(allSurfaceTypesMap).find(([_, value]) => value.toLowerCase() === slug.toLowerCase());
  return entry ? (entry[0] as SurfaceType) : undefined;
};

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownButton = styled.button<{ $iconSize: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-width: ${({ $iconSize }) => `${$iconSize}px`};
  min-height: ${({ $iconSize }) => `${$iconSize}px`};
  width: auto;
  height: auto;
  border: none;
  background: transparent;
  color: ${colors.icon.primary};
  cursor: pointer;
  transition: color 150ms ease;
  padding: 0;

  &:hover {
    color: ${colors.icon.hover};
  }

  svg {
    width: ${NAV_ICON_VISUAL_SIZE}px;
    height: ${NAV_ICON_VISUAL_SIZE}px;
  }

  svg:last-of-type {
    width: 8px;
    height: 8px;
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 50%;
  transform: translateX(-50%);
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  background: ${colors.bg.primary};
  border-radius: 8px;
  box-shadow: ${colors.shadow.toolbar};
  white-space: nowrap;
  z-index: 1001;
`;

const MenuItem = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 6px;
  background: ${({ $isActive }) => ($isActive ? colors.button.selected : 'transparent')};
  color: ${colors.text.primary};
  font-family: ${text.family.primary};
  font-size: ${text.size.primary};
  cursor: pointer;
  transition: background 150ms ease;
  min-width: 150px;

  &:hover {
    background: ${({ $isActive }) => ($isActive ? colors.button.selected : colors.button.hover)};
  }

  svg {
    width: ${NAV_ICON_VISUAL_SIZE}px;
    height: ${NAV_ICON_VISUAL_SIZE}px;
  }
`;

const SurfaceDropdown: React.FC = () => {
  const [currentSurface, setCurrentSurface] = useState<SurfaceType>('Tree');
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { profile } = useProfileContext();

  const allowedSurfaceTypes: SurfaceType[] = ['Tree', 'Graph', 'Flow', 'Focus', 'ReadWrite'];
  if (profile?.isStaff) {
    allowedSurfaceTypes.push('Source Grid');
    allowedSurfaceTypes.push('Flow D3 Force');
  }

  useEffect(() => {
    const path = location.pathname.toLowerCase();
    const slug = path.slice(1);
    const surfaceType = slug2SurfaceType(slug);
    if (surfaceType) {
      setCurrentSurface(surfaceType);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSurfaceChange = (surfaceType: SurfaceType): void => {
    setCurrentSurface(surfaceType);
    const newPath = `/${allSurfaceTypesMap[surfaceType]}${location.search}`;
    navigate(newPath);
    setIsOpen(false);
  };

  const CurrentIcon = surfaceIcons[currentSurface];
  const iconSize = useIconSize('button');

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton $iconSize={iconSize} onClick={() => setIsOpen(!isOpen)} title={currentSurface}>
        <CurrentIcon size={NAV_ICON_VISUAL_SIZE} />
        <ChevronDown />
      </DropdownButton>
      <DropdownMenu $isOpen={isOpen}>
        {allowedSurfaceTypes.map((surfaceType) => {
          const Icon = surfaceIcons[surfaceType];
          return (
            <MenuItem
              key={surfaceType}
              $isActive={surfaceType === currentSurface}
              onClick={() => handleSurfaceChange(surfaceType)}
            >
              <Icon size={NAV_ICON_VISUAL_SIZE} />
              {surfaceType}
            </MenuItem>
          );
        })}
      </DropdownMenu>
    </DropdownContainer>
  );
};

export default SurfaceDropdown;
